// English source strings for module 08: UAE identity and document verification (ADR-017,
// verification stream). Arabic: ar-verification.ts.
export const enVerification = {
  // ------------------------------------------------------------ API messages
  'ver.api.replaceConfirm': 'This person already has accepted identity evidence for this visit. Confirm that the new capture should replace it.',
  'ver.api.transition': 'Identity evidence cannot move from “{from}” to “{to}”.',
  'ver.api.superseded': 'This record was replaced by newer evidence. Open the current record.',
  'ver.api.notReviewable': 'This record is not waiting for review.',
  'ver.api.documentExpired': 'The document has expired. It cannot be accepted.',
  'ver.api.ownCapture': 'You captured this record and it needs a second reviewer. Ask another verifier to decide.',
  'ver.api.ownCorrection': 'You corrected this record and it needs a second reviewer. Ask another verifier to decide.',
  'ver.api.unscannedDocument': 'No malware scan is available for this upload. Accept it only after seeing the original document in person, or reject it.',
  'ver.api.imageDecisionRequired': 'Identity images are still held. Choose what happens to them before turning image storage off.',
  'ver.api.notEntitled': 'UAE PASS is not part of this organisation’s package.',
  'ver.api.methodUnavailable': 'This verification method is not available for this visit.',
  'ver.api.signingUnavailable': 'UAE PASS signing is not enabled.',
  'ver.api.alreadyVerified': 'Your identity is already verified for this visit.',
  'ver.api.providerNotActive': 'UAE PASS is not available right now. Please verify your identity at reception.',
  'ver.api.scopeNotApproved': 'The requested UAE PASS scope is not approved.',
  'ver.api.transactionUnknown': 'This verification link is not valid in this browser. Start again from your visitor pass.',
  'ver.api.transactionFinished': 'This verification attempt has already finished.',
  'ver.api.secretMissing': 'Enter the client secret for this environment first.',
  'ver.api.onboardingIncomplete': 'Production cannot be enabled until every onboarding approval is recorded.',
  'ver.api.goLiveSelfApproved': 'The go-live approval must be recorded by another administrator.',
  'rcp.blocker.identity_verification_required': 'Verified identity is required for this visitor category and is not in place yet.',
  'rcp.warning.identity_evidence_missing': 'No accepted identity verification yet (not required for this category).',

  // ------------------------------------------------------------ validation
  'ver.v.consentRequired': 'Confirm that you agree to share these details.',
  'ver.v.typeNotAllowedHere': 'This document type is not accepted at this site.',
  'ver.v.typeUnknown': 'Choose a configured document type, or “Other document”.',
  'ver.v.mrz.invalid_characters': 'The machine-readable zone contains characters it cannot have.',
  'ver.v.mrz.unsupported_format': 'Enter two lines of 44 characters (passport) or three lines of 30 (ID card).',
  'ver.v.methodDisabled': 'This method is switched off in the verification settings.',
  'ver.v.adapterUnknown': 'This device or adapter is not available.',
  'ver.v.reasonUnsupported': 'Explain why a document type that is not configured is being used.',
  'ver.v.numberInvalid': 'The document number does not pass this document type’s checks.',
  'ver.v.nothingToCorrect': 'Change at least one permitted field.',
  'ver.v.reasonOtherJurisdiction': 'Give a reason: this profile is from another issuing country.',
  'ver.v.settings.duplicate': 'This code is used twice.',
  'ver.v.settings.unknown_validator': 'Unknown validator.',
  'ver.v.settings.validator_mismatch': 'This validator belongs to another country.',
  'ver.v.settings.jurisdiction': 'Use a two-letter country code, or * for any.',
  'ver.v.settings.sealed_not_allowed': 'This field cannot be stored.',
  'ver.v.settings.unknownSite': 'One of the chosen sites does not exist.',
  'ver.v.scopeNotApproved': 'Only approved authentication scopes can be requested.',
  'ver.v.ackNotSignable': 'Only a signature you have accepted can be signed with UAE PASS.',

  // ------------------------------------------------------------ simulator page (development only)
  'ver.sim.title': 'UAE PASS simulator (development)',
  'ver.sim.notice': 'This is a local simulator. No real UAE PASS account or identity is used.',
  'ver.sim.scopes': 'Requested scopes',
  'ver.sim.approveVerified': 'Approve as a verified synthetic account',
  'ver.sim.approveBasic': 'Approve as a basic (unverified) synthetic account',
  'ver.sim.timeout': 'Simulate a provider timeout',
  'ver.sim.cancel': 'Cancel the login',

  // ------------------------------------------------------------ vocabulary
  'nav.identity': 'Identity verification',
  'ver.method.uae_pass': 'UAE PASS',
  'ver.method.emirates_id_reader': 'Emirates ID reader',
  'ver.method.passport_mrz': 'Passport / ID machine-readable zone',
  'ver.method.document_ocr': 'Document scan (OCR)',
  'ver.method.manual': 'Manual check by a verifier',
  'ver.level.none': 'Not needed',
  'ver.level.warn': 'Recommended (warning only)',
  'ver.level.required': 'Required before arrival',
  'ver.proof.identity': 'Identity',
  'ver.proof.nationality': 'Nationality',
  'ver.proof.work_authorisation': 'Work authorisation',
  'ver.attr.given_name': 'Given name',
  'ver.attr.family_name': 'Family name',
  'ver.attr.full_name_ar': 'Name in Arabic',
  'ver.attr.document_number': 'Document number',
  'ver.attr.document_type': 'Document type',
  'ver.attr.issuing_country': 'Issuing country',
  'ver.attr.nationality': 'Nationality',
  'ver.attr.date_of_birth': 'Date of birth',
  'ver.attr.sex': 'Sex',
  'ver.attr.expiry_date': 'Expiry date',
  'ver.attr.provider_subject': 'UAE PASS account identifier',
  'ver.attr.assurance_level': 'UAE PASS account level',
  'ver.attr.mobile': 'Mobile number',
  'ver.attr.email': 'Email address',
  'ver.attr.unrecognised': 'Unrecognised fields',
  'ver.reason.unreadable': 'Could not be read',
  'ver.reason.timeout': 'The device did not answer',
  'ver.reason.unsupported_type': 'Document type not configured',
  'ver.reason.format_invalid': 'Number format not valid',
  'ver.reason.check_digit_invalid': 'Check digit not valid',
  'ver.reason.document_expired': 'Document expired',
  'ver.reason.corrected': 'Fields corrected by a person',
  'ver.reason.rules_unconfirmed': 'Format check only (rules not confirmed)',
  'ver.reason.assurance_below_minimum': 'UAE PASS account level below the minimum',
  'ver.reason.identity_relevant_change': 'Name changed after verification',
  'ver.check.valid': 'Valid',
  'ver.check.invalid': 'Not valid',
  'ver.check.not_applicable': 'Not applicable',
  'ver.check.expired': 'Expired',
  'ver.check.unknown': 'Unknown',
  'ver.check.confirmed': 'Confirmed rules',
  'ver.check.format_only_unconfirmed': 'Format check only',
  'ver.tx.started': 'In progress',
  'ver.tx.completed': 'Completed',
  'ver.tx.cancelled': 'Cancelled',
  'ver.tx.failed': 'Failed',
  'ver.tx.expired': 'Expired',
  'ver.tx.unknown': 'Outcome unknown',
  'ver.limit.not_work_permit': 'Not a work permit',
  'ver.limit.not_nationality_proof': 'Not proof of nationality',
  'ver.limit.unsupported_document_type': 'Document type not configured',
  'ver.match.document': 'Same document',
  'ver.match.name_and_jurisdiction': 'Same name and issuing country',
  'ver.match.name_other_jurisdiction': 'Same name, different issuing country — kept separate until reviewed',
  'ver.match.linked': 'Linked',
  'ver.match.kept_separate': 'Kept separate',
  'ver.doc.content_reviewed': 'Content reviewed',
  'ver.doc.original_sighted': 'Original seen in person',

  // ------------------------------------------------------------ identity panel
  'ver.ui.title': 'Identity verification',
  'ver.ui.sub': 'Identity is its own fact. It never changes approval, presence or access.',
  'ver.ui.requirement': 'This category: {level}',
  'ver.ui.satisfied': 'Identity requirement met',
  'ver.ui.notSatisfied': 'Identity requirement not met',
  'ver.ui.shortfall.method_not_accepted': 'Accepted, but this method is not accepted for the visitor category.',
  'ver.ui.shortfall.proof_missing': 'Accepted, but this document does not prove what the category needs.',
  'ver.ui.none': 'No identity evidence recorded yet.',
  'ver.ui.current': 'Current evidence',
  'ver.ui.previous': 'Earlier records ({n})',
  'ver.ui.method': 'Method',
  'ver.ui.document': 'Document',
  'ver.ui.number': 'Number',
  'ver.ui.expires': 'Expires',
  'ver.ui.checks': 'Checks',
  'ver.ui.checkFormat': 'Format',
  'ver.ui.checkDigits': 'Check digits',
  'ver.ui.checkExpiry': 'Expiry',
  'ver.ui.ruleStatus': 'Rules',
  'ver.ui.proves': 'Proves',
  'ver.ui.limitations': 'Limitations',
  'ver.ui.provenance': 'Source',
  'ver.ui.device': '{model} · firmware {firmware}',
  'ver.ui.firmwareUnvalidated': 'Firmware not validated',
  'ver.ui.simulator': 'Simulator',
  'ver.ui.imageDiscarded': 'Image not kept (policy)',
  'ver.ui.imageStored': 'Image kept under the image policy',
  'ver.ui.discarded': 'Discarded before storage: {fields}',
  'ver.ui.corrected': 'Corrected by a person: {fields}',
  'ver.ui.secondReviewer': 'Needs a second verifier',
  'ver.ui.history': 'History',
  'ver.ui.capturedBy': 'Captured by {name}',
  'ver.ui.capturedByProvider': 'Received from the provider',
  'ver.ui.decidedBy': 'Decided by {name}',
  'ver.ui.decidedBySystem': 'Accepted automatically by policy',
  'ver.ui.capture': 'Capture a document',
  'ver.ui.captureHint': 'Capturing records what the device read for a verifier to review. It is never a verified result on its own.',
  'ver.ui.adapter': 'Device or source',
  'ver.ui.scenario': 'Simulator result',
  'ver.ui.scenario.valid': 'Readable document',
  'ver.ui.scenario.unreadable': 'Unreadable chip or image',
  'ver.ui.scenario.expired': 'Expired document',
  'ver.ui.scenario.bad_check_digit': 'Wrong check digit',
  'ver.ui.scenario.timeout': 'Device does not answer',
  'ver.ui.mrz': 'Machine-readable zone',
  'ver.ui.mrzHint': 'Two lines of 44 characters (passport) or three lines of 30 (ID card). A handheld scanner can type it here.',
  'ver.ui.documentType': 'Document type',
  'ver.ui.otherType': 'Other document (not configured)',
  'ver.ui.replaceCurrent': 'Replace the accepted evidence with this new record',
  'ver.ui.captureSubmit': 'Record capture',
  'ver.ui.manual': 'Manual verification',
  'ver.ui.manualHint': 'Record what you checked when electronic verification is unavailable or not required. Only verifiers can record a result.',
  'ver.ui.observedGiven': 'Given name as on the document',
  'ver.ui.observedFamily': 'Family name as on the document',
  'ver.ui.observedNumber': 'Document number',
  'ver.ui.observedExpiry': 'Expiry date',
  'ver.ui.jurisdiction': 'Issuing country (two letters)',
  'ver.ui.outcome': 'Outcome',
  'ver.ui.outcome.verified': 'Verified',
  'ver.ui.outcome.rejected': 'Rejected',
  'ver.ui.outcome.unavailable': 'Could not verify',
  'ver.ui.reason': 'Reason',
  'ver.ui.manualSubmit': 'Record verification',
  'ver.ui.decide': 'Review decision',
  'ver.ui.accept': 'Accept',
  'ver.ui.reject': 'Reject',
  'ver.ui.unavailable': 'Mark unavailable',
  'ver.ui.correct': 'Correct fields',
  'ver.ui.correctHint': 'Corrections are marked on the record. With the default settings another verifier must accept a corrected record.',
  'ver.ui.correctSubmit': 'Save corrections',
  'ver.ui.reveal': 'Show full number',
  'ver.ui.revealReason': 'Why do you need the full number?',
  'ver.ui.revealed': 'Full number (shown once, recorded in the audit log)',
  'ver.ui.hide': 'Hide',
  'ver.ui.matches': 'Possible existing profiles',
  'ver.ui.matchesHint': 'Suggestions only. Shared names or contact details are not proof of identity; nothing is linked until you confirm.',
  'ver.ui.findMatches': 'Look for matching profiles',
  'ver.ui.noMatches': 'No matching profiles you can see.',
  'ver.ui.lastVisit': 'Last visit {date}',
  'ver.ui.link': 'Link as the same person',
  'ver.ui.separate': 'Keep separate',
  'ver.ui.documents': 'Visitor documents',
  'ver.ui.documentsHint': 'Uploaded before the visit and held in quarantine. Opening one is recorded in the audit log.',
  'ver.ui.open': 'Open',
  'ver.ui.reviewMethod': 'How did you review it?',
  'ver.ui.transactions': 'UAE PASS attempts',
  'ver.ui.image': 'View image',

  // ------------------------------------------------------------ verification page
  'ver.page.eyebrow': 'Administration',
  'ver.page.intro': 'Use evidence provenance, expiry and reviewer judgement — not a successful upload.',
  'ver.tab.queue': 'Review queue',
  'ver.tab.settings': 'Settings',
  'ver.tab.uaepass': 'UAE PASS',
  'ver.queue.empty': 'Nothing is waiting for identity review.',
  'ver.queue.visitor': 'Visitor',
  'ver.queue.item': 'Evidence',
  'ver.queue.status': 'State',
  'ver.queue.received': 'Received',
  'ver.queue.review': 'Review',
  'ver.queue.kind.evidence': 'Identity record',
  'ver.queue.kind.document': 'Uploaded document',
  'ver.set.methods': 'Verification methods',
  'ver.set.methodsHint': 'Manual verification by a verifier is always available.',
  'ver.set.types': 'Document types',
  'ver.set.typesHint': 'Each type uses its own versioned validator. The Emirates ID rules are never applied to another country’s document.',
  'ver.set.type.code': 'Code',
  'ver.set.type.label': 'Name',
  'ver.set.type.kind': 'Kind',
  'ver.set.type.jurisdiction': 'Country',
  'ver.set.type.validator': 'Validator',
  'ver.set.type.proves': 'Proves',
  'ver.set.type.enabled': 'Enabled',
  'ver.set.type.sites': 'Sites',
  'ver.set.type.allSites': 'All sites',
  'ver.set.type.noSites': 'No site yet',
  'ver.set.type.autoVerify': 'Accept automatically when every check passes',
  'ver.set.kind.national_id': 'National ID',
  'ver.set.kind.passport': 'Passport',
  'ver.set.kind.driving_licence': 'Driving licence',
  'ver.set.kind.residence_permit': 'Residence permit',
  'ver.set.extraction': 'Fields kept',
  'ver.set.extractionHint': 'Only these fields are stored for the purpose below. Every other field a document or provider returns is discarded before storage and never logged.',
  'ver.set.purpose': 'Purpose',
  'ver.set.discardedPreview': 'Discarded: {fields}',
  'ver.set.images': 'Identity-document images',
  'ver.set.imagesHint': 'Off by default. When on, images are kept in protected storage and only verifiers can open them.',
  'ver.set.imagesStore': 'Keep identity-document images',
  'ver.set.retentionClass': 'Retention class',
  'ver.set.retentionDays': 'Keep for (days)',
  'ver.set.retentionUndecided': 'Retention period not decided',
  'ver.set.heldImages': '{n} images are held.',
  'ver.set.heldDecision': 'What should happen to the images already held?',
  'ver.set.held.retain_until_retention': 'Keep them until their retention ends',
  'ver.set.held.request_disposal': 'Request their disposal now (carried out by the privacy process)',
  'ver.set.review': 'Review rules',
  'ver.set.secondCorrections': 'A different verifier must accept corrected records',
  'ver.set.secondManual': 'A different verifier must confirm manual verifications',
  'ver.set.unscanned': 'Allow accepting uploaded documents that could not be malware-scanned, after reviewing their content',
  'ver.set.uaepass': 'UAE PASS results',
  'ver.set.autoAccept': 'Accept provider-verified identities automatically',
  'ver.set.minimumAssurance': 'Minimum UAE PASS account level',
  'ver.set.signing': 'Allow UAE PASS document signing',
  'ver.set.evidenceRetention': 'Keep identity evidence for (days)',
  'ver.set.saved': 'Verification settings saved.',
  'ver.set.adapters': 'Devices and adapters',
  'ver.set.adaptersHint': 'No reader hardware or OCR vendor is chosen yet. Simulators are local and never verify a real person.',
  'ver.up.notEntitled': 'UAE PASS is not part of this organisation’s package. The settings stay visible so an administrator can disable what exists.',
  'ver.up.environments': 'Environments',
  'ver.up.environmentsHint': 'Staging and production use separate credentials. Secrets are write-only.',
  'ver.up.environment': 'Environment',
  'ver.up.env.staging': 'Staging',
  'ver.up.env.production': 'Production',
  'ver.up.adapter': 'Adapter',
  'ver.up.issuer': 'Issuer',
  'ver.up.clientId': 'Client ID',
  'ver.up.clientSecret': 'Client secret',
  'ver.up.secretSet': 'A secret is stored. Leave empty to keep it.',
  'ver.up.scopes': 'Authentication scopes',
  'ver.up.scopesHint': 'Only identity authentication is requested. Signing, eSeal and document sharing are separate and off.',
  'ver.up.save': 'Save environment',
  'ver.up.activate': 'Use this environment',
  'ver.up.disable': 'Disable',
  'ver.up.disableReason': 'Reason for disabling',
  'ver.up.status.configured': 'Configured',
  'ver.up.status.active': 'In use',
  'ver.up.status.disabled': 'Disabled',
  'ver.up.notConfigured': 'Not configured',
  'ver.up.onboarding': 'Production onboarding',
  'ver.up.onboardingHint': 'Production needs the service-provider approval, the agreement, a passed staging assessment and a go-live approval by another administrator.',
  'ver.up.item.service_provider_approval': 'Service-provider approval',
  'ver.up.item.agreement': 'Agreement',
  'ver.up.item.staging_assessment': 'Staging assessment',
  'ver.up.item.go_live_approval': 'Go-live approval',
  'ver.up.outcome.approved': 'Approved',
  'ver.up.outcome.passed': 'Passed',
  'ver.up.outcome.signed': 'Signed',
  'ver.up.outcome.rejected': 'Rejected',
  'ver.up.outcome.failed': 'Failed',
  'ver.up.reference': 'Reference',
  'ver.up.note': 'Note',
  'ver.up.record': 'Record',
  'ver.up.gateOk': 'Production can be enabled.',
  'ver.up.gateMissing': 'Still needed for production: {items}',
  'ver.up.gateSelf': 'The go-live approval was recorded by you; another administrator must enable production.',
  'ver.up.recordedBy': '{name}, {date}',

  // ------------------------------------------------------------ visitor pass and return page
  'ver.pass.title': 'Verify your identity',
  'ver.pass.required': 'Your visit needs verified identity before arrival.',
  'ver.pass.optional': 'You can verify your identity now to save time at reception.',
  'ver.pass.verified': 'Your identity is verified.',
  'ver.pass.review': 'Your details are with a verifier.',
  'ver.pass.consent': 'I agree to share my UAE PASS name and Emirates ID details with this organisation for: {purpose}.',
  'ver.pass.start': 'Verify with UAE PASS',
  'ver.pass.starting': 'Opening UAE PASS…',
  'ver.pass.assisted': 'You can also show an identity document at reception.',
  'ver.pass.pending': 'A UAE PASS attempt is still open. You can start again.',
  'ver.ret.title': 'Identity verification',
  'ver.ret.working': 'Checking the result…',
  'ver.ret.verified': 'Your identity is verified. You can close this page or return to your visitor pass.',
  'ver.ret.review_pending': 'We received your details. A verifier will review them before your visit.',
  'ver.ret.cancelled': 'You cancelled the UAE PASS login. Your identity is not verified yet; your visit is not affected.',
  'ver.ret.failed': 'We could not verify your identity this time. You can try again or verify at reception.',
  'ver.ret.unknown': 'UAE PASS did not answer in time. Your identity is not verified yet; you can try again or verify at reception.',
  'ver.ret.signed': 'Your document was signed with UAE PASS.',
  'ver.ret.back': 'Return to your visitor pass',

  // ------------------------------------------------------------ policy editor
  'ver.pol.title': 'Identity verification',
  'ver.pol.hint': 'How strictly reception and the kiosk treat identity for this category.',
  'ver.pol.level': 'Requirement',
  'ver.pol.methods': 'Accepted methods',
  'ver.pol.proofs': 'Must prove',
} as const;
